// Roulette sound — synthesised, not sampled.
//
// This app ships no audio files anywhere: shared-ui/lib/navSound.ts builds its
// chime from oscillators, and this follows the same house pattern. Synthesis
// means zero download, no CSP/asset plumbing, and every sound can be tuned by
// changing a number instead of re-cutting a file.
//
// Every sound is short, quiet and made of at most a handful of nodes, all of
// which are stop()-scheduled so nothing accumulates over a long session.
//
// Browsers gate audio behind a user gesture; `unlock()` is called from the first
// pointerdown in the game and is idempotent.

let _ctx: AudioContext | null = null;
let _enabled = true;

const KEY = 'rlt_sound';
/* VOICE IS ITS OWN SWITCH.
   It never was one: the tile called sCallNumber(21,'red',true) and nothing else,
   so it could not be turned off, it re-announced "number twenty-one, red" every
   time it was touched, and the On/Off line under it was showing the SOUND flag.
   Three separate bugs from one missing piece of state. */
const KEY_V = 'rlt_voice';
let _voiceOn = true;

export function initSound() {
  try {
    const v = localStorage.getItem(KEY);
    if (v != null) _enabled = v === '1';
  } catch { /* localStorage unavailable */ }
  return _enabled;
}
export function setSoundEnabled(on: boolean) {
  _enabled = on;
  try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* ignore */ }
}
export function isSoundEnabled() { return _enabled; }

/** Voice defaults ON, like sound and music. */
export function initVoice() {
  try {
    const v = localStorage.getItem(KEY_V);
    if (v != null) _voiceOn = v === '1';
  } catch { /* private mode */ }
  return _voiceOn;
}
export function isVoiceOn() { return _voiceOn; }
export function setVoiceOn(on: boolean) {
  _voiceOn = on;
  try { localStorage.setItem(KEY_V, on ? '1' : '0'); } catch { /* private mode */ }
  return _voiceOn;
}

/**
 * WHAT THE TILE SAYS WHEN YOU SWITCH IT ON.
 *
 * It used to read out a fake result — "number twenty-one, red" — which is both
 * confusing (nothing has been spun) and, on a table where the last result is on
 * screen, actively misleading. A switch should confirm ITSELF. Two words, spoken
 * in the same voice the calls will use, so turning it on both proves it works and
 * tells you what it is going to sound like.
 *
 * Still routed through the same gesture path, because on iOS this tap is the only
 * moment speech can be unlocked from.
 */
export function sVoiceOn() {
  const sy = synth();
  if (!sy) return;
  try {
    if (sy.speaking || sy.pending) sy.cancel();
    const u = new SpeechSynthesisUtterance('Voice on');
    const v = pickVoice();
    if (v) u.voice = v;
    u.lang = v?.lang || 'en-IN';
    u.rate = 1; u.pitch = 1; u.volume = 1;
    sy.speak(u);
  } catch { /* engine busy */ }
}

/* While iOS speech is running, duckAudio() SUSPENDS the context so the speech
   engine owns the audio session -- and this flag is what keeps it suspended.
   Without it, any sound call during the sentence (a settle chime, the music
   scheduler's half-second pump, anything) hit the auto-resume below and the
   speech went silent mid-word. (2026-08-16 audit, C6.) */
let _speechHold = false;
/* Backgrounded = silent. The ball can take eight seconds to roll; without this
   it kept hissing from the pocket after the user switched apps. (Audit m12.) */
let _hidden = false;

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    /* try/catch, and not as ceremony: sSpinStart() runs between doSpin taking
       its round lock and arming the round's timers. If construction ever threw
       (iOS context-limit edge cases), the throw would escape doSpin with the
       lock held and the table would freeze forever. Audio must degrade to
       silence, never to a stopped game. (Recheck P3.) */
    try { _ctx = new AC(); } catch { return null; }
    document.addEventListener('visibilitychange', () => {
      _hidden = document.hidden;
      if (!_ctx) return;
      if (_hidden) { try { _ctx.suspend().catch(() => {}); } catch { /* torn down */ } }
      else if (!_speechHold) { try { _ctx.resume().catch(() => {}); } catch { /* torn down */ } }
    });
  }
  if (_ctx && _ctx.state === 'suspended' && !_speechHold && !_hidden) _ctx.resume().catch(() => {});
  return _ctx;
}

/**
 * The game's single AudioContext, for the music bed.
 *
 * It has to be THIS one and not a second context of its own: iOS allows very few
 * contexts per page and hands the audio session to one of them, so a second
 * context is the classic way to end up with music that plays on desktop and is
 * silent on a phone.
 */
export function audioCtx(): AudioContext | null { return ctx(); }

/** Warm the context from a real user gesture. Safe to call repeatedly. */
export function unlock() {
  const c = ctx();
  // Respect the speech hold: a tap that lands mid-announcement must not
  // resume the context out from under the sentence.
  if (c && c.state === 'suspended' && !_speechHold && !_hidden) c.resume().catch(() => {});
}

/* ─── primitives ─────────────────────────────────────────────────────────── */

function bus(gain: number) {
  const c = ctx();
  if (!c || !_enabled) return null;
  const g = c.createGain();
  g.gain.value = gain;
  g.connect(c.destination);
  return { c, g };
}

/** A short pitched blip. The workhorse behind most of the UI ticks. */
function tone(freq: number, dur: number, opts: {
  type?: OscillatorType; to?: number; gain?: number; delay?: number; attack?: number;
} = {}) {
  const b = bus(opts.gain ?? 0.13);
  if (!b) return;
  const { c, g } = b;
  const t0 = c.currentTime + (opts.delay ?? 0);
  const o = c.createOscillator();
  o.type = opts.type ?? 'sine';
  o.frequency.setValueAtTime(freq, t0);
  if (opts.to) o.frequency.exponentialRampToValueAtTime(opts.to, t0 + dur);
  const env = c.createGain();
  const a = opts.attack ?? 0.004;
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(1, t0 + a);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(env).connect(g);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

// ── ONE noise buffer, made once and re-used ────────────────────────────────
// Every noise sound used to allocate its own buffer and fill it with
// Math.random() on the calling frame. The ball roll is the expensive one: a 7 s
// hiss is ~336,000 samples generated synchronously on the exact frame the ball
// launches, once a round, for as long as the session lasts (2026-08-15 thermal
// audit, finding 13 — raised then, never verified because the run hit its
// limit; confirmed still live 2026-09-06).
//
// White noise is white noise: one buffer serves every caller, and short sounds
// start at a random offset inside it so repeated chips still differ. The cache
// grows to the longest duration asked for (capped at 12 s, the ball's own cap)
// and is rebuilt only if the AudioContext is replaced. Audibly identical, and
// after the first sound the per-round cost is zero.
let _noiseCtx: BaseAudioContext | null = null;
let _noiseBuf: AudioBuffer | null = null;
function noiseBuffer(c: BaseAudioContext, seconds: number): AudioBuffer {
  const want = Math.max(1, Math.floor(c.sampleRate * Math.min(Math.max(seconds, 0.5), 12)));
  if (_noiseCtx === c && _noiseBuf && _noiseBuf.length >= want) return _noiseBuf;
  const buf = c.createBuffer(1, want, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < want; i++) d[i] = Math.random() * 2 - 1;
  _noiseCtx = c; _noiseBuf = buf;
  return buf;
}
/** A start offset that keeps repeated short sounds from being byte-identical. */
function noiseOffset(buf: AudioBuffer, dur: number) {
  const room = buf.duration - dur - 0.01;
  return room > 0 ? Math.random() * room : 0;
}

/** Filtered noise — the basis of every "physical" sound here (chip, ball). */
function noise(dur: number, opts: {
  f0?: number; f1?: number; q?: number; gain?: number; delay?: number; type?: BiquadFilterType;
} = {}) {
  const b = bus(opts.gain ?? 0.12);
  if (!b) return;
  const { c, g } = b;
  const t0 = c.currentTime + (opts.delay ?? 0);
  const buf = noiseBuffer(c, dur);
  const off = noiseOffset(buf, dur);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = opts.type ?? 'bandpass';
  f.frequency.setValueAtTime(opts.f0 ?? 1400, t0);
  if (opts.f1) f.frequency.exponentialRampToValueAtTime(opts.f1, t0 + dur);
  f.Q.value = opts.q ?? 1.2;
  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(1, t0 + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(env).connect(g);
  src.start(t0, off);
  src.stop(t0 + dur + 0.02);
}

/* ─── the game's voice ───────────────────────────────────────────────────── */

/** Picking a token up off the tray — a light, dry click. */
export const sChipPick = () => { noise(0.05, { f0: 2600, f1: 1500, q: 2, gain: 0.09 }); };

/** A chip landing on the felt: the clack of clay, then a tiny settle. */
export const sChipPlace = () => {
  noise(0.055, { f0: 2200, f1: 900, q: 1.6, gain: 0.16 });
  tone(320, 0.09, { type: 'triangle', to: 190, gain: 0.09, delay: 0.012 });
};

/**
 * A FAKE chip landing — the crowd's chips, not the player's.
 *
 * Same clack, quieter and duller, so a busy table sounds busy without ever
 * competing with the player's own placement. Token-bucketed to 3 per second:
 * the bombardment fires ~15 times a second and 15 overlapping clacks is not
 * "engaging", it is a buzz, and it would also mask the one sound the player
 * needs to hear — their own bet landing.
 */
let faintBudget = 3;
let faintAt = 0;
export const sChipPlaceFaint = () => {
  const now = performance.now();
  faintBudget = Math.min(3, faintBudget + (now - faintAt) / 333);
  faintAt = now;
  if (faintBudget < 1) return;
  faintBudget -= 1;
  // ±15ms of jitter so simultaneous landings never phase into one hard click
  const d = Math.random() * 0.015;
  noise(0.04, { f0: 1700, f1: 700, q: 1.4, gain: 0.055, delay: d });
  tone(280, 0.06, { type: 'triangle', to: 170, gain: 0.03, delay: d + 0.01 });
};

/** Undo / clear — the same clack played backwards in feel: low then gone. */
export const sChipOff = () => {
  noise(0.05, { f0: 900, f1: 2000, q: 1.6, gain: 0.1 });
};

/** Nothing happened, and the player needs to know why. */
export const sDenied = () => {
  tone(220, 0.1, { type: 'square', to: 160, gain: 0.08 });
  tone(165, 0.14, { type: 'square', to: 120, gain: 0.07, delay: 0.09 });
};

/** SPIN: the table closing — a rising whoosh with a low thud under it. */
export const sSpinStart = () => {
  noise(0.5, { f0: 300, f1: 2600, q: 0.9, gain: 0.1 });
  tone(120, 0.34, { type: 'sine', to: 62, gain: 0.16 });
};

/** The ball on the track: a soft rolling hiss that falls in pitch as it slows.
 *  Returns a stop() so the caller can end it exactly when the ball drops. */
export function sBallRoll(seconds: number) {
  const b = bus(0.075);
  if (!b) return () => {};
  const { c, g } = b;
  const t0 = c.currentTime;
  const dur = Math.min(seconds, 12);
  const buf = noiseBuffer(c, dur);           // cached: no 336k-sample fill on the launch frame
  const off = noiseOffset(buf, dur);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.setValueAtTime(1900, t0);
  f.frequency.exponentialRampToValueAtTime(520, t0 + seconds);
  f.Q.value = 3.5;
  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(1, t0 + 0.25);
  env.gain.setValueAtTime(1, t0 + seconds * 0.75);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + seconds);
  src.connect(f).connect(env).connect(g);
  src.start(t0, off);
  src.stop(t0 + seconds + 0.05);
  return () => { try { src.stop(); } catch { /* already stopped */ } };
}

/** The ball leaving the track and rattling across the deflectors and frets. */
export const sBallDrop = () => {
  for (let i = 0; i < 7; i++) {
    noise(0.035, { f0: 2600 - i * 180, q: 3, gain: 0.11 - i * 0.011, delay: i * 0.062 + Math.random() * 0.02 });
  }
};

/** Settled in a pocket. */
export const sSettle = () => {
  noise(0.06, { f0: 1200, f1: 500, q: 2, gain: 0.13 });
  tone(440, 0.12, { type: 'triangle', to: 300, gain: 0.08, delay: 0.03 });
};

/** WIN — a bright major arpeggio with a shimmer on top. Deliberately short:
 *  it plays every winning round, and anything longer becomes a nuisance. */
/** WIN — a real fanfare, not a chirp. The owner heard "no special audio" and
 *  he was half right: the old sting existed but was short and sat UNDER the
 *  music bed. Now: a rising C-major arpeggio, answered an octave up, a G5
 *  crown held on top, and a shimmer — ~1.1s of unmistakable event (the bed is
 *  ducked under it from the caller). Still bounded: a win should feel like a
 *  bell, not an advert. */
export const sWin = () => {
  const notes = [523.25, 659.25, 783.99, 1046.5];   // C5 E5 G5 C6
  notes.forEach((f, i) => tone(f, 0.34, { type: 'triangle', gain: 0.16, delay: i * 0.08 }));
  // the answer, an octave up and quicker — call-and-response is what makes it
  // read as a fanfare rather than a scale
  [1046.5, 1318.5, 1568].forEach((f, i) => tone(f, 0.26, { type: 'triangle', gain: 0.11, delay: 0.34 + i * 0.06 }));
  tone(1568, 0.6, { type: 'sine', gain: 0.07, delay: 0.52 });         // the crown, held
  tone(784, 0.55, { type: 'sine', gain: 0.05, delay: 0.5 });          // its octave root
  noise(0.65, { f0: 5000, f1: 9500, q: 0.7, gain: 0.045, delay: 0.4 }); // shimmer
};

/** LOSE — two soft descending notes. Quiet on purpose: the player already
 *  knows, and rubbing it in is how a casino game starts feeling hostile. */
export const sLose = () => {
  // Three-step descent with a soft low landing — clearly an event now (it was
  // inaudible under the bed), still gentle: the player already knows, and
  // rubbing it in is how a casino game starts feeling hostile.
  tone(392, 0.2, { type: 'sine', to: 330, gain: 0.10 });
  tone(294, 0.24, { type: 'sine', to: 247, gain: 0.09, delay: 0.16 });
  tone(196, 0.4, { type: 'sine', to: 165, gain: 0.08, delay: 0.34 });
};

/** A tick as the aim crosses from one bet to another. Very quiet — it fires
 *  a lot, and it is meant to be felt more than heard. */
export const sAimTick = () => { tone(1750, 0.022, { type: 'square', gain: 0.028 }); };

/**
 * THE LAST FIVE SECONDS — one pip per number as the window closes.
 *
 * It CLIMBS. Five and four get a low, soft pip; three, two and one get a
 * brighter, harder one a fourth above it. A countdown that ticks at one pitch
 * measures time; a countdown that rises tells the player to decide, and it does
 * that without a word or a colour, which matters because their eyes are on the
 * numbers and not on the clock.
 *
 * Deliberately short and well under the chip sounds: this fires five times a
 * round, every round, and anything louder becomes something to switch off.
 */
export const sCountPip = (n: number) => {
  const hot = n <= 3;
  tone(hot ? 1170 : 780, hot ? 0.085 : 0.070,
       { type: 'triangle', to: hot ? 900 : 640, gain: hot ? 0.070 : 0.052 });
};

/* ═══ THE CALL ══════════════════════════════════════════════════════════
 *
 * "Number thirteen, black." Spoken as the result card slides in, for all 37
 * pockets — the dealer's call, which is the one thing a real table always does
 * out loud and this one did not.
 *
 * WHY speechSynthesis AND NOT AUDIO FILES. This app ships no audio anywhere;
 * every sound in it is synthesised (see the note at the top of this file). A
 * spoken call cannot be built from oscillators, and 37 numbers x 3 colours of
 * recorded voice is 40-odd assets that do not exist and cannot be generated
 * here. The Web Speech API is on every browser this ships to, costs nothing to
 * download, and speaks whatever we hand it. If a device turns out to have no
 * voices at all it simply says nothing — see the guard below.
 *
 * IT OBEYS THE SAME SWITCH AS EVERYTHING ELSE. `_enabled` is the game's own
 * sound toggle, so turning sound off silences the call too. A voice that keeps
 * talking after the player has muted the game is the fastest way to get an app
 * deleted.
 */

/** Cached once — getVoices() is empty until the list loads, and re-scanning it
 *  on every call is a synchronous walk over every voice the OS has. */
let _voice: SpeechSynthesisVoice | null | undefined;
/** Set only when an utterance has ACTUALLY been heard to start — see below. */
let _verified = false;

function synth(): SpeechSynthesis | null {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;
  try { return window.speechSynthesis; } catch { return null; }
}

const isIOS = () =>
  typeof navigator !== 'undefined'
  && (/iP(hone|ad|od)/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1));

/**
 * Pick a voice — and on iOS, prefer one that is ON THE DEVICE.
 *
 * getVoices() lists voices the system knows about, including ones whose data
 * has never been downloaded. Handing speak() one of those is a silent no-op on
 * iOS: no error, no `start`, no sound. `localService` is the flag that says the
 * voice is actually installed, so it is required rather than merely preferred.
 *
 * If nothing local matches we return null and let the SYSTEM choose from
 * `u.lang` alone, which is always safe. Setting `.voice` is the risky half.
 */
function pickVoice(): SpeechSynthesisVoice | null {
  if (_voice !== undefined) return _voice;
  const sy = synth();
  if (!sy) return null;
  try {
    const all = sy.getVoices();
    // NOT cached when empty: the list loads asynchronously, so an empty read
    // has to be retried rather than remembered as "no voices".
    if (!all.length) return null;
    const local = all.filter((v) => v.localService !== false);
    const pool = local.length ? local : all;
    const en = (v: SpeechSynthesisVoice) => v.lang.replace('_', '-').toLowerCase();
    _voice = pool.find((v) => en(v) === 'en-in')
      || pool.find((v) => en(v).startsWith('en'))
      || null;                       // deliberately NOT `|| all[0]`
  } catch {
    _voice = null;
  }
  return _voice ?? null;
}

/* ── THE WEBAUDIO INTERACTION ────────────────────────────────────────────
 * On iOS an AudioContext that is RUNNING owns the audio session, and speech
 * synthesis routed alongside it can come out silent — no error, nothing in the
 * console, it simply is not heard. This game keeps a context alive for the chip
 * and ball sounds from the first tap onward, which is exactly the condition.
 *
 * The call lands 1200 ms after the settle sound, so nothing of ours is playing
 * at that moment: suspending the context for the length of one short sentence
 * costs nothing and is the one lever available from script. It is resumed on
 * `end`, on `error`, and by a hard timeout, so there is no path where the
 * context stays suspended and the rest of the game goes quiet. */
function duckAudio(): () => void {
  const c = _ctx;
  if (!c || c.state !== 'running' || !isIOS()) return () => {};
  let done = false;
  _speechHold = true;               // nothing may auto-resume until the call ends
  try { c.suspend().catch(() => {}); } catch { /* ignore */ }
  const restore = () => {
    if (done) return;
    done = true;
    _speechHold = false;
    if (!_hidden) { try { c.resume().catch(() => {}); } catch { /* ignore */ } }
  };
  window.setTimeout(restore, 6000);   // belt and braces: never stay suspended
  return restore;
}

/**
 * UNLOCK THE SPEECH ENGINE FROM A REAL GESTURE.
 *
 * iOS speaks only if the FIRST utterance of a session was issued synchronously
 * inside a user gesture; every later call then works from anywhere. A result
 * arrives on a timer twenty seconds after the last tap, so without this the
 * announcements are simply dropped there.
 *
 * TWO THINGS THAT WERE WRONG IN THE FIRST VERSION, both iOS-specific:
 *   - it primed with a SPACE at volume 0. WebKit has nothing to say for
 *     whitespace and skips the utterance outright, so the gesture was spent
 *     without the engine ever being unlocked. It now primes with a real
 *     character at a real (if tiny) volume.
 *   - it set `_spoke` when speak() was CALLED, which proves nothing. It now
 *     waits for the engine's own `start` event, and until that has been seen it
 *     re-primes on every gesture rather than assuming one worked.
 */
export function unlockSpeech() {
  if (_verified) return;
  const sy = synth();
  if (!sy) return;
  try {
    const u = new SpeechSynthesisUtterance('.');
    u.volume = 0.02;                 // audible to the engine, inaudible to a human
    u.rate = 2;                      // and over as fast as it can be
    u.onstart = () => { _verified = true; };
    sy.speak(u);
    pickVoice();                     // the list is usually ready by the first tap
  } catch { /* no engine — the card still shows the number */ }
}

/** What the device WILL say for a pocket. Exported so the wording can be
 *  asserted for all 37 without a speaker in the room. */
export function callTextFor(n: number, colour: 'red' | 'black' | 'green') {
  return `Number ${n}, ${colour}`;
}

/** Diagnostics for the Settings > "Test the voice" row, so a device that will
 *  not speak can say WHY instead of just staying quiet. */
export function speechStatus() {
  const sy = synth();
  if (!sy) return 'not supported';
  let n = 0;
  try { n = sy.getVoices().length; } catch { /* ignore */ }
  if (!n) return 'loading voices';
  const v = pickVoice();
  if (!v) return 'no English voice';
  return _verified ? v.name : `${v.name} (untested)`;
}

/**
 * Announce a result. `colour` comes from the engine's own colourOf(), so the
 * call can never disagree with the disc the player is looking at.
 *
 * `force` is set by the Settings test row, which must speak even when the
 * result-time guards would not.
 */
export function sCallNumber(
  n: number, colour: 'red' | 'black' | 'green', force = false,
) {
  // NOT gated on _enabled: Voice is its OWN switch, not a sub-switch of
  // Sounds. The old gate meant Sounds OFF silenced the announcer while the
  // Voice tile still said On -- a switch that lies. (Audit m11.)
  // THE VOICE SWITCH. Separate from the sound switch on purpose: plenty of
  // players want the chips and the ball without an announcer, and a few want the
  // announcer with the table quiet.
  if (!_voiceOn && !force) return;
  if (!force && typeof document !== 'undefined' && document.hidden) return;
  const sy = synth();
  if (!sy) return;
  try {
    // CANCEL ONLY IF SOMETHING IS ACTUALLY QUEUED. An unconditional cancel()
    // right before speak() loses the utterance on several Android builds, and
    // on older iOS it can wedge the engine for the rest of the session.
    if (sy.speaking || sy.pending) sy.cancel();
    if (sy.paused) sy.resume();

    const unduck = duckAudio();
    const say = (retry: boolean) => {
      const u = new SpeechSynthesisUtterance(callTextFor(n, colour));
      const v = pickVoice();
      // Only ever pin a voice we know is installed; otherwise lang alone, and
      // let the system pick. See pickVoice().
      if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = 'en-IN'; }
      u.rate = 0.98;      // a shade under natural — a called number is said once
      u.pitch = 0.95;     // and a croupier is not chirpy
      u.volume = 1;
      let started = false;
      u.onstart = () => { started = true; _verified = true; };
      u.onend = unduck;
      u.onerror = unduck;
      sy.speak(u);
      // ONE RETRY. iOS drops the first utterance after a long silence often
      // enough to matter, and it does so without firing `error` — the only
      // signal is that `start` never comes.
      if (retry) {
        window.setTimeout(() => {
          if (!started && !sy.speaking && !sy.pending) say(false);
        }, 350);
      }
    };
    say(true);
  } catch { /* no voices, or the engine refused — the card still says it */ }
}

/** The voice list arrives asynchronously on most browsers. Ask for it at
 *  start-up so the FIRST call of a session already has a voice picked rather
 *  than falling back to the system default for one round. */
export function primeVoices() {
  const sy = synth();
  if (!sy) return;
  try {
    sy.getVoices();
    // Not `once`: some browsers fire this several times as engines register,
    // and the better voice may arrive on a later one.
    sy.addEventListener?.('voiceschanged', () => { _voice = undefined; });
  } catch { /* ignore */ }
}

/** Generic UI button. */
export const sTap = () => { tone(880, 0.045, { type: 'sine', to: 620, gain: 0.07 }); };
